import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendUp, Wallet, Tag as TagIcon, ChartPie, DownloadSimple, Funnel, CalendarBlank, Check, ArrowCounterClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format, differenceInDays, addDays, parse, isWithinInterval } from 'date-fns';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import api from '../api';
import { useFilters } from '../context/FilterContext';

const CHART_COLORS = ['#0ea5e9', '#22c55e', '#eab308', '#f97316', '#ef4444', '#8b5cf6', '#ec4899'];

const Dashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [categories, setCategories] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Use filter context for persistent filters
  const { dashboardFilters, setDashboardFilters, resetDashboardFilters } = useFilters();
  const { selectedCategories, startDate, endDate } = dashboardFilters;
  
  // Local UI state for pickers
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);

  // Helper functions to update filters
  const setSelectedCategories = (updater) => {
    setDashboardFilters(prev => ({
      ...prev,
      selectedCategories: typeof updater === 'function' ? updater(prev.selectedCategories) : updater
    }));
  };

  const setStartDate = (date) => {
    setDashboardFilters(prev => ({ ...prev, startDate: date }));
  };

  const setEndDate = (date) => {
    setDashboardFilters(prev => ({ ...prev, endDate: date }));
  };

  useEffect(() => {
    fetchCategories();
    fetchTransactions();
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [selectedCategories, startDate, endDate]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      // Sort alphabetically with ETC always last
      const sorted = response.data.sort((a, b) => {
        if (a.name === 'ETC') return 1;
        if (b.name === 'ETC') return -1;
        return a.name.localeCompare(b.name);
      });
      setCategories(sorted);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const fetchTransactions = async () => {
    try {
      // Fetch all transactions for accurate calculations
      const response = await api.get('/transactions', { params: { limit: 10000 } });
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to load transactions');
    }
  };

  const fetchAnalytics = async () => {
    try {
      const params = {};
      // Only send date filter when BOTH start and end dates are specified
      if (startDate && endDate) {
        params.start_date = format(startDate, 'yyyy-MM-dd');
        params.end_date = format(endDate, 'yyyy-MM-dd');
      }
      
      const response = await api.get('/analytics/dashboard', { params });
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryName) => {
    setSelectedCategories(prev => 
      prev.includes(categoryName) 
        ? prev.filter(c => c !== categoryName)
        : [...prev, categoryName]
    );
  };

  const clearFilters = () => {
    resetDashboardFilters();
  };

  // Only apply date filter when BOTH dates are specified
  const hasDateFilter = startDate && endDate;
  const hasFilters = selectedCategories.length > 0 || hasDateFilter;

  // Check if date range is at least 7 days (inclusive)
  const showWeeklyTrend = useMemo(() => {
    if (!startDate || !endDate) return false;
    const daysDiff = differenceInDays(endDate, startDate) + 1; // +1 for inclusive
    return daysDiff >= 7;
  }, [startDate, endDate]);

  // Calculate dynamic interval based on date range (max 10 data points)
  const trendInterval = useMemo(() => {
    if (!startDate || !endDate) return { days: 7, label: 'Weekly' };
    const totalDays = differenceInDays(endDate, startDate) + 1;
    
    // Calculate how many data points we'd have with weekly intervals
    const weeklyPoints = Math.ceil(totalDays / 7);
    
    if (weeklyPoints <= 10) {
      return { days: 7, label: 'Weekly' };
    } else if (Math.ceil(totalDays / 10) <= 10) {
      return { days: 10, label: '10-Day' };
    } else if (Math.ceil(totalDays / 14) <= 10) {
      return { days: 14, label: 'Fortnightly' };
    } else {
      return { days: 30, label: 'Monthly' };
    }
  }, [startDate, endDate]);

  // Calculate trend data with dynamic interval (max 10 data points)
  const weeklyTrendData = useMemo(() => {
    if (!showWeeklyTrend || !startDate || !endDate || transactions.length === 0) return [];
    
    const intervalDays = trendInterval.days;
    const periods = [];
    let currentStart = startDate;
    let periodNum = 1;
    
    while (currentStart <= endDate && periods.length < 10) {
      const currentEnd = addDays(currentStart, intervalDays - 1);
      const actualEnd = currentEnd > endDate ? endDate : currentEnd;
      
      // Filter transactions for this period
      let periodTotal = 0;
      transactions.forEach(trans => {
        try {
          // Parse transaction date (DD/MM/YYYY format)
          const transDate = parse(trans.date, 'dd/MM/yyyy', new Date());
          
          // Check if transaction is within this period and matches category filter
          if (isWithinInterval(transDate, { start: currentStart, end: actualEnd })) {
            if (selectedCategories.length === 0 || selectedCategories.includes(trans.category)) {
              periodTotal += trans.amount;
            }
          }
        } catch (e) {
          // Skip invalid dates
        }
      });
      
      // Dynamic label based on interval
      let label;
      if (intervalDays === 7) {
        label = `W${periodNum}`;
      } else if (intervalDays === 30) {
        label = format(currentStart, 'MMM');
      } else {
        label = format(currentStart, 'dd/MM');
      }
      
      periods.push({
        week: label,
        label: `${format(currentStart, 'dd/MM')} - ${format(actualEnd, 'dd/MM')}`,
        amount: periodTotal
      });
      
      currentStart = addDays(currentStart, intervalDays);
      periodNum++;
    }
    
    // Add trend direction for coloring - compare each point to previous
    for (let i = 0; i < periods.length; i++) {
      if (i === 0) {
        // First point - check direction to next point
        periods[i].isUp = periods.length > 1 ? periods[1].amount >= periods[0].amount : true;
      } else {
        // Compare to previous point
        periods[i].isUp = periods[i].amount >= periods[i-1].amount;
      }
    }
    
    return periods;
  }, [showWeeklyTrend, startDate, endDate, transactions, selectedCategories, trendInterval]);

  // Calculate overall trend color based on regression slope (maroon for up, blue for down)
  const trendColor = useMemo(() => {
    if (weeklyTrendData.length < 2) return '#000000';
    
    const n = weeklyTrendData.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
    
    weeklyTrendData.forEach((point, index) => {
      sumX += index;
      sumY += point.amount;
      sumXY += index * point.amount;
      sumX2 += index * index;
    });
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    return slope >= 0 ? '#800000' : '#0066cc';
  }, [weeklyTrendData]);

  // Create combined chart data with trend line values using linear regression
  const chartDataWithTrend = useMemo(() => {
    if (weeklyTrendData.length < 2) return weeklyTrendData;
    
    const n = weeklyTrendData.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
    
    weeklyTrendData.forEach((point, index) => {
      sumX += index;
      sumY += point.amount;
      sumXY += index * point.amount;
      sumX2 += index * index;
    });
    
    // Calculate slope and intercept for best fit line
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    
    // Add trend value to each data point
    return weeklyTrendData.map((point, index) => ({
      ...point,
      trend: intercept + slope * index
    }));
  }, [weeklyTrendData]);

  const generatePDF = async () => {
    // Check if weekly trend is visible
    const hasWeeklyTrend = showWeeklyTrend && weeklyTrendData.length > 0 && weeklyTrendData.some(w => w.amount > 0);
    
    // Use landscape if we have both charts, portrait if only pie chart
    const doc = hasWeeklyTrend ? new jsPDF('landscape', 'mm', 'a4') : new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('TRACK CC - Dashboard Report', pageWidth / 2, 15, { align: 'center' });

    // Date and filters
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    let subtitle = `Generated: ${new Date().toLocaleDateString('en-IN')}`;
    if (hasFilters) {
      const filterText = [];
      if (selectedCategories.length > 0) filterText.push(`Categories: ${selectedCategories.join(', ')}`);
      if (startDate && endDate) filterText.push(`${format(startDate, 'dd/MM/yy')} - ${format(endDate, 'dd/MM/yy')}`);
      subtitle += ` | ${filterText.join(' | ')}`;
    }
    doc.text(subtitle, pageWidth / 2, 22, { align: 'center' });

    // Summary box
    doc.setFillColor(245, 245, 245);
    doc.rect(10, 28, pageWidth - 20, 12, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total Spend: ${analytics?.total_spend?.toLocaleString('en-IN', {maximumFractionDigits: 0})}`, 20, 35);
    doc.text(`High Value Transactions: ${analytics?.high_value_count || 0}`, pageWidth / 2, 35);

    try {
      const chartY = 45;
      
      // Helper function to draw legend manually with small circles
      const drawLegend = (startX, startY, data, colors, maxWidth) => {
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        const circleRadius = 1.5;
        const lineHeight = 4;
        const colWidth = maxWidth / 2;
        
        data.forEach((item, index) => {
          const col = index % 2;
          const row = Math.floor(index / 2);
          const x = startX + (col * colWidth);
          const y = startY + (row * lineHeight);
          
          // Draw small circle
          const color = colors[index % colors.length];
          const r = parseInt(color.slice(1, 3), 16);
          const g = parseInt(color.slice(3, 5), 16);
          const b = parseInt(color.slice(5, 7), 16);
          doc.setFillColor(r, g, b);
          doc.circle(x + circleRadius, y - 1, circleRadius, 'F');
          
          // Draw text aligned with circle
          doc.setTextColor(0, 0, 0);
          const name = item.name.length > 12 ? item.name.substring(0, 12) + '..' : item.name;
          doc.text(name, x + circleRadius * 2 + 2, y);
          doc.text(item.value.toLocaleString('en-IN', {maximumFractionDigits: 0}), x + colWidth - 5, y, { align: 'right' });
        });
        
        return Math.ceil(data.length / 2) * lineHeight;
      };
      
      if (hasWeeklyTrend) {
        // Landscape: charts side by side
        const chartHeight = pageHeight - chartY - 15;
        const chartWidth = (pageWidth - 30) / 2;

        // Pie Chart on left - capture only the SVG
        const pieChartSvg = document.querySelector('[data-testid="category-breakdown-card"] .recharts-wrapper');
        if (pieChartSvg) {
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text('Category Breakdown', 10 + chartWidth / 2, chartY, { align: 'center' });
          
          const canvas = await html2canvas(pieChartSvg, { scale: 2, backgroundColor: '#ffffff' });
          const imgData = canvas.toDataURL('image/png');
          const svgHeight = Math.min(chartHeight - 35, (canvas.height * chartWidth) / canvas.width);
          doc.addImage(imgData, 'PNG', 10, chartY + 5, chartWidth, svgHeight);
          
          // Draw legend manually below chart
          drawLegend(12, chartY + svgHeight + 10, pieData, CHART_COLORS, chartWidth - 4);
        }

        // Weekly Trend on right
        const weeklyTrendSvg = document.querySelector('[data-testid="weekly-trend-card"] .recharts-wrapper');
        if (weeklyTrendSvg) {
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text('Weekly Trend', 10 + chartWidth + 10 + chartWidth / 2, chartY, { align: 'center' });
          
          const canvas = await html2canvas(weeklyTrendSvg, { scale: 2, backgroundColor: '#ffffff' });
          const imgData = canvas.toDataURL('image/png');
          const imgHeight = Math.min(chartHeight - 10, (canvas.height * chartWidth) / canvas.width);
          doc.addImage(imgData, 'PNG', 10 + chartWidth + 10, chartY + 5, chartWidth, imgHeight);
        }
      } else {
        // Portrait: only pie chart centered
        const pieChartSvg = document.querySelector('[data-testid="category-breakdown-card"] .recharts-wrapper');
        if (pieChartSvg) {
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text('Category Breakdown', pageWidth / 2, chartY, { align: 'center' });
          
          const canvas = await html2canvas(pieChartSvg, { scale: 2, backgroundColor: '#ffffff' });
          const imgData = canvas.toDataURL('image/png');
          const chartWidth = pageWidth - 40;
          const svgHeight = Math.min(pageHeight - chartY - 50, (canvas.height * chartWidth) / canvas.width);
          doc.addImage(imgData, 'PNG', 20, chartY + 5, chartWidth, svgHeight);
          
          // Draw legend manually below chart
          drawLegend(22, chartY + svgHeight + 10, pieData, CHART_COLORS, chartWidth - 4);
        }
      }

      // Footer
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text('Generated by TRACK CC', pageWidth / 2, pageHeight - 5, { align: 'center' });

      // Use blob URL method for better compatibility with preview environments
      const pdfBlob = doc.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'dashboard-report.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  // Pie data - show selected categories + "Others" when categories are selected
  const pieData = useMemo(() => {
    if (!analytics?.category_breakdown) return [];
    
    const allData = analytics.category_breakdown.map((item) => ({
      name: item._id,
      value: item.amount,
    }));
    
    // If no categories selected, show all
    if (selectedCategories.length === 0) {
      return allData;
    }
    
    // Calculate selected categories and others
    let othersTotal = 0;
    const selectedData = [];
    
    allData.forEach(item => {
      if (selectedCategories.includes(item.name)) {
        selectedData.push(item);
      } else {
        othersTotal += item.value;
      }
    });
    
    // Add "Others" if there are unselected categories
    if (othersTotal > 0) {
      selectedData.push({ name: 'Others', value: othersTotal });
    }
    
    return selectedData;
  }, [analytics, selectedCategories]);

  // Calculate filtered totals for summary cards
  const filteredTotals = useMemo(() => {
    // When no category filter is selected, use API values
    if (selectedCategories.length === 0) {
      return {
        totalSpend: analytics?.total_spend || 0,
        highValueCount: analytics?.high_value_count || 0
      };
    }
    
    // When categories are selected, calculate from pie data
    const totalSpend = pieData
      .filter(item => item.name !== 'Others')
      .reduce((sum, item) => sum + item.value, 0);
    
    // High value count - filter transactions by selected categories
    let highValueCount = 0;
    const threshold = analytics?.high_value_threshold || 9000;
    transactions.forEach(trans => {
      if (trans.amount >= threshold && selectedCategories.includes(trans.category)) {
        highValueCount++;
      }
    });
    
    return { totalSpend, highValueCount };
  }, [analytics, pieData, transactions, selectedCategories]);

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="dashboard-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-muted rounded-xl"></div>
          <div className="h-64 bg-muted rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="p-6 space-y-6"
      data-testid="dashboard-page"
    >
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
          Dashboard
        </h1>
        <p className="text-sm text-muted-foreground uppercase tracking-wider">
          Your Spending Overview
        </p>
      </div>

      {/* Filters */}
      <Card data-testid="filters-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-base">
              <Funnel size={18} weight="bold" />
              Filters
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={clearFilters} 
              className="h-8 px-2"
              data-testid="reset-all-filters-btn"
            >
              <ArrowCounterClockwise size={16} className="mr-1" />
              Reset All
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <Label className="text-xs">Categories (select multiple)</Label>
            <Popover open={showCategoryPicker} onOpenChange={setShowCategoryPicker}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal h-10"
                  data-testid="category-filter"
                >
                  <TagIcon size={16} className="mr-2" />
                  {selectedCategories.length === 0 
                    ? 'All Categories' 
                    : `${selectedCategories.length} selected`}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2" align="start">
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => toggleCategory(cat.name)}
                      className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded hover:bg-muted transition-colors"
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center ${selectedCategories.includes(cat.name) ? 'bg-primary border-primary' : 'border-muted-foreground'}`}>
                        {selectedCategories.includes(cat.name) && <Check size={12} className="text-white" />}
                      </div>
                      {cat.name}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs">From Date</Label>
              <Popover open={showStartPicker} onOpenChange={setShowStartPicker}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal h-10"
                    data-testid="start-date-picker"
                  >
                    <CalendarBlank size={16} className="mr-2" />
                    {startDate ? format(startDate, 'dd/MM/yy') : 'Start'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => {
                      setStartDate(date);
                      setShowStartPicker(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs">To Date</Label>
              <Popover open={showEndPicker} onOpenChange={setShowEndPicker}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal h-10"
                    data-testid="end-date-picker"
                  >
                    <CalendarBlank size={16} className="mr-2" />
                    {endDate ? format(endDate, 'dd/MM/yy') : 'End'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={(date) => {
                      setEndDate(date);
                      setShowEndPicker(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4">
        <Card data-testid="total-spend-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase text-muted-foreground tracking-wider">
              Total Spend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Wallet size={24} className="text-[#800000]" weight="bold" />
              <p className="text-lg font-bold tabular-nums text-[#800000]">
                {filteredTotals.totalSpend.toLocaleString('en-IN', {maximumFractionDigits: 0})}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="high-value-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase text-muted-foreground tracking-wider">
              High Value
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <TrendUp size={24} className="text-[#800000]" weight="bold" />
              <p className="text-lg font-bold tabular-nums text-[#800000]">
                {filteredTotals.highValueCount}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Download PDF Button - only show if there's data */}
      {analytics && pieData.length > 0 && filteredTotals.totalSpend > 0 && (
        <Button
          onClick={generatePDF}
          className="w-full rounded-full"
          data-testid="download-dashboard-pdf-button"
        >
          <DownloadSimple size={20} weight="bold" className="mr-2" />
          Download Dashboard Report
        </Button>
      )}

      {/* Category Breakdown - hide if total spend is zero */}
      {pieData.length > 0 && filteredTotals.totalSpend > 0 && (
        <Card data-testid="category-breakdown-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <ChartPie size={20} weight="bold" />
              Category Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={false}
                  innerRadius={50}
                  outerRadius={90}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value.toLocaleString('en-IN')}`} />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-1" data-testid="category-legend">
              {pieData.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                  />
                  <span className="text-xs font-medium truncate">{item.name}</span>
                  <span className="text-xs font-bold tabular-nums ml-auto flex-shrink-0">{item.value.toLocaleString('en-IN', {maximumFractionDigits: 0})}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Weekly Trend - only show when date filters are set with 7+ days difference and has data */}
      {showWeeklyTrend && weeklyTrendData.length > 0 && weeklyTrendData.some(w => w.amount > 0) && (
        <Card data-testid="weekly-trend-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <TrendUp size={20} weight="bold" />
              {trendInterval.label} Trend
              <span className="ml-auto text-xs font-normal" style={{ color: trendColor }}>
                {trendColor === '#800000' ? '↑ Upward' : '↓ Downward'}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={chartDataWithTrend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={50} />
                <Tooltip 
                  formatter={(value, name) => [value?.toLocaleString('en-IN') || '', name === 'trend' ? 'Trend' : 'Actual']}
                  labelFormatter={(label, payload) => payload?.[0]?.payload?.label || label}
                />
                {/* Bold regression trend line (maroon for up, blue for down) */}
                <Line 
                  type="linear" 
                  dataKey="trend" 
                  stroke={trendColor}
                  strokeWidth={4}
                  dot={false}
                  isAnimationActive={false}
                  name="Trend"
                />
                {/* Thin black line connecting actual data points */}
                <Line 
                  type="linear" 
                  dataKey="amount" 
                  stroke="#000000"
                  strokeWidth={1}
                  dot={{ r: 5, fill: '#000000', stroke: 'white', strokeWidth: 2 }}
                  activeDot={{ r: 7, fill: '#000000', stroke: 'white', strokeWidth: 2 }}
                  isAnimationActive={false}
                  name="Actual"
                />
              </LineChart>
            </ResponsiveContainer>
            <p className="text-xs text-muted-foreground text-center mt-1">
              <span className="inline-block w-4 h-1 mr-1 align-middle" style={{ backgroundColor: trendColor }}></span>Regression trend
            </p>
          </CardContent>
        </Card>
      )}

      {analytics?.category_breakdown?.length === 0 && (
        <Card className="text-center p-12" data-testid="no-data-card">
          <TagIcon size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No transactions yet. Upload a statement to get started.</p>
        </Card>
      )}
    </motion.div>
  );
};

export default Dashboard;
